import { Card, CardContent, Skeleton, cn } from '@sr/ui-library';
import { SkeletonRegion } from '../SkeletonRegion';
import { OnboardingHeading } from './OnboardingHeading';

/**
 * Loading placeholders for the MSR3 onboarding flow (APP-2237).
 *
 * Each skeleton mirrors the geometry of the thing it stands in for — same
 * grid, same card padding, same control heights — so the swap to the loaded UI
 * is a change of colour rather than a jump of layout. Bars sit inside
 * fixed-height boxes rather than being sized to the bar itself: a 12px bar
 * centred in the 20px line box of the label it replaces holds the exact space
 * the real text will take.
 */

/**
 * Bar centred in the line box of the text it stands in for. The caller sizes
 * the line box — `h-5` for `text-sm`, plus a width — through `className`; the
 * bar fills it.
 */
const TextSkeleton = ({ className, ...props }: React.ComponentProps<'div'>) => (
  <div className={cn('flex items-center', className)} {...props}>
    <Skeleton className="h-3 w-full rounded" />
  </div>
);

/**
 * Uniform label widths read as a machine-drawn grid, so cycle a few widths.
 * Indexed rather than random to keep renders deterministic.
 */
const LABEL_WIDTHS = ['w-24', 'w-16', 'w-28', 'w-20'];

/**
 * A `FormItem`: label above input. `h-5` is the `text-sm` label line box and
 * `h-9` is ui-library's Input height, so the pair occupies the real field's
 * space exactly.
 */
const FieldSkeleton = ({ index }: { index: number }) => (
  <div className="space-y-2">
    <TextSkeleton className={cn('h-5', LABEL_WIDTHS[index % 4])} />

    <Skeleton className="h-9 w-full" />
  </div>
);

type OnboardingSkeletonSection = {
  /**
   * Trailing action inside the card, for steps that keep Save & Continue in
   * the card footer rather than below the form (the profile step).
   */
  cardAction?: boolean;
  /** Wide-container column count. Matches the section it stands in for. */
  columns?: 2 | 3;
  fields: number;
};

/** Real section headings differ in length ("Entity Details" vs "Contact"). */
const HEADING_WIDTHS = ['w-32', 'w-20', 'w-24'];

const SectionSkeleton = ({
  cardAction,
  columns = 2,
  fields,
  headingWidth,
}: OnboardingSkeletonSection & { headingWidth: string }) => (
  <section className="space-y-4">
    {/* The section's h2 — `text-base`, so a 24px line box. */}
    <TextSkeleton className={cn('h-6', headingWidth)} />

    <Card className="rounded-lg border-border shadow-none @container">
      <CardContent
        className={cn(
          'grid grid-cols-1 gap-x-4 gap-y-6 p-5',
          columns === 3 ? '@2xl:grid-cols-3' : '@2xl:grid-cols-2',
        )}
      >
        {Array.from({ length: fields }).map((_, index) => (
          <FieldSkeleton key={index} index={index} />
        ))}

        {cardAction && (
          <div className="col-span-full flex justify-end">
            <Skeleton className="h-9 w-36" />
          </div>
        )}
      </CardContent>
    </Card>
  </section>
);

type OnboardingFormSkeletonProps = {
  /**
   * `split` mirrors the Back / Save & Continue row that sits below the form on
   * the workspace step; `none` is for steps whose action lives in the card.
   */
  footer?: 'none' | 'split';
  sections: OnboardingSkeletonSection[];
};

/**
 * Placeholder for a card-and-fields onboarding step. The page heading is left
 * to the page itself — it is static text, so it can paint immediately and only
 * the data-dependent body below it needs a placeholder.
 */
const OnboardingFormSkeleton = ({
  footer = 'none',
  sections,
}: OnboardingFormSkeletonProps) => (
  <SkeletonRegion label="Loading form" className="space-y-8">
    {sections.map((section, index) => (
      <SectionSkeleton
        key={index}
        {...section}
        headingWidth={HEADING_WIDTHS[index % HEADING_WIDTHS.length]}
      />
    ))}

    {footer === 'split' && (
      <div className="flex justify-between pt-2">
        <Skeleton className="h-9 w-40" />

        <Skeleton className="h-9 w-36" />
      </div>
    )}
  </SkeletonRegion>
);

/**
 * A whole step's placeholder: its heading above its body. The three form steps
 * are the same shape, so the differences between them stay visible as the one
 * `className` each passes — the bank step's `mx-auto max-w-3xl` mirrors that
 * page's own width, the other two run full width like theirs.
 */
const OnboardingStepSkeletonShell = ({
  children,
  className,
  heading,
  ...props
}: React.ComponentProps<'div'> & { heading: string }) => (
  <div className={cn('space-y-6', className)} {...props}>
    <OnboardingHeading>{heading}</OnboardingHeading>

    {children}
  </div>
);

/** Heading of the profile step, shared by the step and its Suspense fallback. */
export const PROFILE_STEP_HEADING = 'Create Your Profile';

/** One section, the three name fields, Save & Continue inside the card. */
const PROFILE_FORM_SECTIONS: OnboardingSkeletonSection[] = [
  { cardAction: true, columns: 3, fields: 3 },
];

/** Body of the profile step, for the wait on the user record. */
export const OnboardingProfileFormSkeleton = () => (
  <OnboardingFormSkeleton sections={PROFILE_FORM_SECTIONS} />
);

/** Whole profile step — see OnboardingBankStepSkeleton. */
export const OnboardingProfileStepSkeleton = () => (
  <OnboardingStepSkeletonShell heading={PROFILE_STEP_HEADING}>
    <OnboardingProfileFormSkeleton />
  </OnboardingStepSkeletonShell>
);

/** Heading of the workspace step, shared with its Suspense fallback. */
export const WORKSPACE_STEP_HEADING = 'Set Up Your Workspace';

/** Field counts mirror the Entity / Contact / Address sections of the form. */
const WORKSPACE_FORM_SECTIONS: OnboardingSkeletonSection[] = [
  { fields: 6 },
  { fields: 2 },
  { fields: 4 },
];

/** Body of the workspace step, for the wait on the workspace record. */
export const OnboardingWorkspaceFormSkeleton = () => (
  <OnboardingFormSkeleton footer="split" sections={WORKSPACE_FORM_SECTIONS} />
);

/** Whole workspace step — see OnboardingBankStepSkeleton. */
export const OnboardingWorkspaceStepSkeleton = () => (
  <OnboardingStepSkeletonShell
    heading={WORKSPACE_STEP_HEADING}
    className="space-y-8"
  >
    <OnboardingWorkspaceFormSkeleton />
  </OnboardingStepSkeletonShell>
);

/** The block of inputs Airwallex renders inside its iframe. */
const BankFieldsSkeleton = () => (
  <div className="grid grid-cols-1 gap-x-4 gap-y-6 @2xl:grid-cols-2">
    {Array.from({ length: 6 }).map((_, index) => (
      <FieldSkeleton key={index} index={index} />
    ))}
  </div>
);

/**
 * Placeholder for the hosted fields on their own, for the wait *inside*
 * AirwallexBeneficiaryForm while the SDK boots. The step's real Back / Skip /
 * Save row is already on screen by then, so this one deliberately omits the
 * action row that OnboardingBankSkeleton draws — two action rows, one grey and
 * one live, would read as a broken page.
 */
export const OnboardingBankFieldsSkeleton = () => (
  <SkeletonRegion label="Loading bank details" className="@container">
    <BankFieldsSkeleton />
  </SkeletonRegion>
);

/**
 * Placeholder for the bank step. The fields are rendered by Airwallex inside
 * an iframe, so this stands in for the whole hosted form: a block of inputs
 * and the step's own action row.
 */
export const OnboardingBankSkeleton = () => (
  <SkeletonRegion label="Loading bank details">
    <Card className="rounded-lg border-border shadow-none @container">
      <CardContent className="space-y-6 p-5">
        <BankFieldsSkeleton />

        {/* Back sits left, Skip and Save right — the MSR3 layout of
            AirwallexBeneficiaryForm's action row. */}
        <div className="flex items-center justify-between pt-2">
          <Skeleton className="h-9 w-40" />

          <div className="flex gap-3">
            <Skeleton className="h-9 w-28" />

            <Skeleton className="h-9 w-36" />
          </div>
        </div>
      </CardContent>
    </Card>
  </SkeletonRegion>
);

/** Heading of the bank step, shared by the step and its Suspense fallback. */
export const BANK_STEP_HEADING = 'Provide Bank Details For Payment';

/**
 * Whole bank step including its heading — the Suspense fallback for the lazily
 * loaded step. Rendering the same skeleton the step itself renders makes the
 * chunk-fetch wait and the data-fetch wait one continuous placeholder instead
 * of a full-screen spinner that is then replaced by a skeleton.
 */
export const OnboardingBankStepSkeleton = () => (
  <OnboardingStepSkeletonShell
    heading={BANK_STEP_HEADING}
    className="mx-auto max-w-3xl"
  >
    <OnboardingBankSkeleton />
  </OnboardingStepSkeletonShell>
);

/** Step count — see OnboardingStepsSkeleton. */
const SKELETON_STEP_COUNT = 3;

/**
 * Placeholder for OnboardingStepsV3.
 *
 * The structure is a copy of the real stepper's — including the connectors
 * hidden at either end, which keep the row of markers centred — so nothing
 * moves when the steps fill in.
 */
export const OnboardingStepsSkeleton = () => (
  <SkeletonRegion label="Loading onboarding progress" className="w-full">
    <div className="mx-auto flex w-full max-w-3xl items-start">
      {Array.from({ length: SKELETON_STEP_COUNT }).map((_, index) => (
        <div key={index} className="flex min-w-0 flex-1 flex-col items-center">
          <div className="flex w-full items-center">
            <Skeleton
              className={cn(
                'h-0.5 flex-1 rounded-none',
                index === 0 && 'invisible',
              )}
            />

            <Skeleton className="size-8 shrink-0 rounded-full" />

            <Skeleton
              className={cn(
                'h-0.5 flex-1 rounded-none',
                index === SKELETON_STEP_COUNT - 1 && 'invisible',
              )}
            />
          </div>

          {/* The step label — `text-xs`/`text-sm`, so a 20px line box. */}
          <div className="mt-2 flex h-5 items-center">
            <Skeleton className="h-3 w-14 rounded" />
          </div>
        </div>
      ))}
    </div>
  </SkeletonRegion>
);
